import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import "./SharedTable.css";
import { Autocomplete, Button, CircularProgress, Box, Chip, OutlinedInput, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import { FiEdit2, FiTrash2, FiEye, FiUpload, FiX, FiZap, FiRotateCcw } from "react-icons/fi";
import { VscRefresh } from "react-icons/vsc";
import FilterDropdown from "./Common Components/FilterDropdown";
import useCurrentProject, { resolveProject } from "./Common Components/useCurrentProject";

// Persisted UI state keyed by component name; survives navigate-and-back
const CR_STATE_KEY = 'fixdesk:clientReqState:v1';
const CR_DATA_CACHE_KEY = 'fixdesk:clientReqData:v1';
const CR_DATA_TTL_MS = 5 * 60 * 1000; // 5 minutes

const loadPersisted = () => {
    try {
        const raw = sessionStorage.getItem(CR_STATE_KEY);
        if (!raw) return {};
        return JSON.parse(raw) || {};
    } catch { return {}; }
};

const savePersisted = (next) => {
    try { sessionStorage.setItem(CR_STATE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
};

const loadCachedRequirements = (projectId) => {
    try {
        const raw = sessionStorage.getItem(CR_DATA_CACHE_KEY);
        if (!raw) return null;
        const cache = JSON.parse(raw);
        if (!cache || String(cache.projectId) !== String(projectId)) return null;
        if (Date.now() - (cache.ts || 0) > CR_DATA_TTL_MS) return null;
        return cache.data || null;
    } catch { return null; }
};

const saveCachedRequirements = (projectId, data) => {
    try {
        sessionStorage.setItem(CR_DATA_CACHE_KEY, JSON.stringify({
            projectId: String(projectId), ts: Date.now(), data,
        }));
    } catch { /* ignore */ }
};

const ClientRequirements = () => {
    const navigate = useNavigate();
    const priorities = ["High", "Medium", "Low"];

    // Hydrate persisted UI state once
    const persisted = useMemo(() => loadPersisted(), []);

    const [sprints, setSprints] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [clientStatus, setClientStatus] = useState([]);
    const [clientRequirements, setClientRequirements] = useState([]);
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useCurrentProject();
    const [showPopup, setShowPopup] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [editRequirementId, seteditRequirementId] = useState(null);
    const [file, setFile] = useState(null);
    const [open, setOpen] = useState(false);
    const [filterClient, setFilterClient] = useState(persisted.filterClient || []);
    const [filterStatus, setFilterStatus] = useState(persisted.filterStatus || []);
    const [filterCreatedBy, setFilterCreatedBy] = useState(persisted.filterCreatedBy || []);
    const [filterAssignedTo, setFilterAssignedTo] = useState(persisted.filterAssignedTo || []);
    const [filterPriority, setFilterPriority] = useState(persisted.filterPriority || []);
    const [requirementDetails, setRequirementDetails] = useState({
            client_id: "",
            status_id: "",
            requirement: "",
            description: "",
            assigned_to_id: "",
            sprint_id: '',
            priority: '',
            user_story:"",
            acceptance_criteria:""
        });
    const [attachmentFiles, setAttachmentFiles] = useState([]);
    const [impactAreas, setImpactAreas] = useState([]);
    const [impactAreaOptions, setImpactAreaOptions] = useState([]);
    const [showConfirmGenerate, setShowConfirmGenerate] = useState(false);
    const [generatingArtifact, setGeneratingArtifact] = useState(false);
    const [crSearch, setCrSearch] = useState(persisted.crSearch || '');
    const [loadingRequirements, setLoadingRequirements] = useState(false);
    const [revokeConfirm, setRevokeConfirm] = useState(null); // requirement obj or null
    const [statusUpdatingId, setStatusUpdatingId] = useState(null); // requirement id being status-edited
    const [priorityTypes, setPriorityTypes] = useState([]);
    const [showMoveDialog, setShowMoveDialog] = useState(false);
    const [moveDetails, setMoveDetails] = useState({ sprint_id: '', ticket_type: 'Story', priority_id: '' });
    const [movingToSprint, setMovingToSprint] = useState(false);
    const descRef = useRef(null);

    // Persist UI state whenever any of these change. The project selection is
    // deliberately excluded — useCurrentProject owns it, shared across pages.
    useEffect(() => {
        savePersisted({
            filterClient, filterStatus, filterCreatedBy,
            filterAssignedTo, filterPriority, crSearch,
        });
    }, [filterClient, filterStatus, filterCreatedBy,
        filterAssignedTo, filterPriority, crSearch]);

    // ── Client-side filtering across all filters + search ──
    // Filters are applied locally; the server fetch only happens when the
    // project changes (or refresh is triggered). This eliminates network
    // round-trips for every filter chip click and prevents the loading flash.
    const crFiltered = useMemo(() => {
        const matchesId = (rowId, list) =>
            !list.length || list.some(v => String(v) === String(rowId));
        const matchesPriority = (val) =>
            !filterPriority.length || filterPriority.some(v => String(v).toLowerCase() === String(val || '').toLowerCase());
        const q = crSearch.trim().toLowerCase();

        return clientRequirements.filter(r => {
            if (!matchesId(r.client_id, filterClient)) return false;
            if (!matchesId(r.status_id, filterStatus)) return false;
            if (!matchesId(r.cre_id, filterCreatedBy)) return false;
            if (!matchesId(r.assigned_to_id, filterAssignedTo)) return false;
            if (!matchesPriority(r.priority)) return false;
            if (!q) return true;
            return (
                (r.requirement_code || '').toLowerCase().includes(q) ||
                (r.requirement || '').toLowerCase().includes(q) ||
                (r.client_name || '').toLowerCase().includes(q) ||
                (r.status_name || '').toLowerCase().includes(q) ||
                (r.assigned_to_name || '').toLowerCase().includes(q) ||
                (r.created_by || '').toLowerCase().includes(q)
            );
        });
    }, [clientRequirements, crSearch, filterClient, filterStatus,
        filterCreatedBy, filterAssignedTo, filterPriority]);

    const getClientDetails = useCallback(async () => {
        return fetch(`${API_BASE_URL}client_requirements/client/`, {
        method: 'GET',
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
        })
        .then((res) => res.json())
        .then((data) => {
            if (data.status === "success") {
            setAllClients(data.data || []);
            } else if (data.status === "fail") {
            showErrorToast(data.message);
            }
        })
        .catch((err) => {
            console.error(err.message);
            return [];
        });
    }, []);

    // Clients scoped to the selected project, so a requirement can only ever be
    // raised against a client that belongs to it. With no project selected the
    // full list is shown.
    const clients = useMemo(() => {
        if (!currentProject) return allClients;
        return allClients.filter(c => String(c.project_id) === String(currentProject));
    }, [allClients, currentProject]);

    const getClientStatus = useCallback(async () => {
        return fetch(`${API_BASE_URL}client_requirements/clientstatus/`, {
        method: 'GET',
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
        })
        .then((res) => res.json())
        .then((data) => {
            if (data.status === "success") {
            setClientStatus(data.data);
            } else if (data.status === "fail") {
            showErrorToast(data.message);
            }
        })
        .catch((err) => {
            console.error(err.message);
            return [];
        });
    }, []);

    const getProjects = useCallback(() => {
        fetch(`${API_BASE_URL}core/project/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        }).then(res => res.json())
            .then(data => {
                if (data.status === "success") {
                    setProjects(data.data);
                    setCurrentProject(prev => resolveProject(data.data, prev));
                } else {
                    showErrorToast(data.message);
                }
            })
            .catch(err => {
                console.error(err);
                showErrorToast("Failed to fetch projects");
            });
    }, [])

    const getEmployees = useCallback(() => {
        fetch(`${API_BASE_URL}core/employee/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.status === "success") {
                    setEmployees(data.data);
                } else {
                    showErrorToast("Failed to load employees");
                }
            })
            .catch((err) => console.error("Error fetching employees:", err));
    })

    const getSprints = useCallback((project_id) => {
        fetch(`${API_BASE_URL}ticketing/sprint/?project_id=${project_id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        })
            .then(res => res.json())
            .then(data => {
                if (data.status === "success" && Array.isArray(data.data)) {
                    const sorted = data.data.sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
                    setSprints(sorted);
                } else {
                    showErrorToast(data.message);
                }
            })
            .catch(err => {
                console.error(err);
                showErrorToast("Failed to fetch sprints");
            });
    }, []);

    // Fetch all requirements for the project — filters are applied client-side.
    // `forceRefresh=false` returns cached data when fresh (within TTL).
    const getClientRequiremnets = useCallback(async (projectId, { forceRefresh = false } = {}) => {
        if (!projectId) {
            setClientRequirements([]);
            return;
        }
        if (!forceRefresh) {
            const cached = loadCachedRequirements(projectId);
            if (cached) {
                setClientRequirements(cached);
                return;
            }
        }
        setLoadingRequirements(true);
        try {
            const res = await fetch(
                `${API_BASE_URL}client_requirements/requirements/?project_id=${projectId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                }
            );
            const data = await res.json();
            if (data.status === "success") {
                setClientRequirements(data.data || []);
                saveCachedRequirements(projectId, data.data || []);
            } else {
                showErrorToast(data.message);
            }
        } catch (err) {
            console.error(err);
            showErrorToast("Failed to fetch requirements");
        } finally {
            setLoadingRequirements(false);
        }
    }, []);

    useEffect(() => {
        if (currentProject) {
            getClientRequiremnets(currentProject);
        } else {
            setClientRequirements([]);
        }
    }, [currentProject, getClientRequiremnets]);

    useEffect(() => {
        if (currentProject)  {
            getSprints(currentProject);
        } else {
            setSprints([]);
        }
    }, [currentProject]);

    const getImpactAreas = useCallback((projectId) => {
        if (!projectId) {
            setImpactAreaOptions([]);
            return;
        }
        fetch(`${API_BASE_URL}client_requirements/impact-areas/?project_id=${projectId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        })
        .then(res => res.json())
        .then(data => {
            if (data.status === "success") {
                setImpactAreaOptions(data.data.filter(a => a.is_active).map(a => a.name));
            }
        })
        .catch(err => console.error("Error fetching impact areas:", err));
    }, []);

    const getPriorityTypes = useCallback(() => {
        fetch(`${API_BASE_URL}core/priorityType/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        })
            .then(res => res.json())
            .then(data => {
                if (data.status === "success") setPriorityTypes(data.data || []);
            })
            .catch(err => console.error("Error fetching priority types:", err));
    }, []);

    useEffect(() => {
        getClientDetails();
        getClientStatus();
        getProjects();
        getEmployees();
        getPriorityTypes();
    }, []);

    useEffect(() => {
        if (currentProject) {
            getImpactAreas(currentProject);
        }
    }, [currentProject]);

    const handleAdd = () => {
        getEmployees();
        seteditRequirementId(null);
        setAttachmentFiles([]);
        setImpactAreas([]);
        if (currentProject) getImpactAreas(currentProject);
        setShowPopup(true);
    }
    const handleChange = (e) => {
        setRequirementDetails(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
        // Auto-expand textareas (description, user story, acceptance criteria)
        if (e.target.tagName === 'TEXTAREA') {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
        }
    }

    const clearRequirementDeatils = () => {
        setRequirementDetails({
            client_id: "",
            status_id: "",
            requirement: "",
            description: "",
            assigned_to_id: "",
            sprint_id: '',
            priority: '',
            user_story:"",
            acceptance_criteria:""
        });
        setAttachmentFiles([]);
        setImpactAreas([]);
        seteditRequirementId(null);
    };

    const handleSubmit = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ ...requirementDetails })
            });
            const data = await res.json();
            if (data.status === "success") {
                const savedId = data.id || editRequirementId;
                // Upload attachments if any
                if (attachmentFiles.length > 0 && savedId) {
                    for (const file of attachmentFiles) {
                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("requirement_id", savedId);
                        await fetch(`${API_BASE_URL}client_requirements/requirements/attachments/`, {
                            method: "POST",
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                            body: formData,
                        });
                    }
                }
                showSuccessToast(editRequirementId ? "Requirement updated!" : "Requirement added!");
                setShowPopup(false);
                getSprints(currentProject);
                getClientRequiremnets(currentProject, { forceRefresh: true });
                clearRequirementDeatils();
                return savedId;
            } else {
                showErrorToast(data.message);
                return null;
            }
        } catch (err) {
            console.error(err);
            showErrorToast("Save failed");
            return null;
        }
    }

    const handleSaveAndGenerate = async () => {
        // Save first
        const res = await fetch(`${API_BASE_URL}client_requirements/requirements/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify({ ...requirementDetails })
        });
        const data = await res.json();
        if (data.status !== "success") {
            showErrorToast(data.message || "Save failed");
            return;
        }
        const savedId = data.id || editRequirementId;

        // Upload attachments
        if (attachmentFiles.length > 0 && savedId) {
            for (const file of attachmentFiles) {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("requirement_id", savedId);
                await fetch(`${API_BASE_URL}client_requirements/requirements/attachments/`, {
                    method: "POST",
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                    body: formData,
                });
            }
        }

        showSuccessToast("Requirement saved. Generating artifacts...");
        setShowConfirmGenerate(false);
        setShowPopup(false);
        setGeneratingArtifact(true);

        // Generate artifact
        try {
            const genRes = await fetch(`${API_BASE_URL}client_requirements/requirements/generate/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ id: savedId, impact_areas: impactAreas }),
            });
            const genData = await genRes.json();
            setGeneratingArtifact(false);
            if (genData.status === "success") {
                showSuccessToast("Artifacts generated!");
                clearRequirementDeatils();
                navigate(`/ClientRequirements/${savedId}`);
            } else {
                showErrorToast(genData.message || "Generation failed");
                getClientRequiremnets(currentProject, { forceRefresh: true });
                clearRequirementDeatils();
            }
        } catch (err) {
            setGeneratingArtifact(false);
            showErrorToast("Artifact generation failed");
            getClientRequiremnets(currentProject, { forceRefresh: true });
            clearRequirementDeatils();
        }
    }

    const handleEdit = (requirement) => {
        setRequirementDetails(requirement);
        seteditRequirementId(requirement.id);
        setImpactAreas(requirement.impact_areas || []);
        setAttachmentFiles([]);
        getEmployees();
        if (currentProject) getImpactAreas(currentProject);
        setShowPopup(true);
    }

    // Inline status edit — allowed regardless of generation status or sprint state.
    const handleStatusChange = async (requirement, newStatusId) => {
        if (!newStatusId || String(newStatusId) === String(requirement.status_id)) return;
        setStatusUpdatingId(requirement.id);
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/status/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ id: requirement.id, status_id: newStatusId }),
            });
            const data = await res.json();
            if (data.status === "success") {
                showSuccessToast("Status updated");
                const newStatusName = clientStatus.find(s => String(s.id) === String(newStatusId))?.client_requirement_status;
                setClientRequirements(prev => {
                    const next = prev.map(r => r.id === requirement.id
                        ? { ...r, status_id: Number(newStatusId), status_name: newStatusName || r.status_name }
                        : r);
                    saveCachedRequirements(currentProject, next);
                    return next;
                });
            } else {
                showErrorToast(data.message || "Status update failed");
            }
        } catch (err) {
            console.error(err);
            showErrorToast("Status update failed");
        } finally {
            setStatusUpdatingId(null);
        }
    };

    const handleDelete = (requirementId) => {
        fetch(`${API_BASE_URL}client_requirements/requirements/?id=${requirementId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
        })
            .then(res => res.json())
            .then(data => {
                if (data.status === "success") {
                    showSuccessToast("Requirement Deleted!");
                    getSprints(currentProject);
                    getClientRequiremnets(currentProject, { forceRefresh: true });
                } else {
                    showErrorToast(data.message);
                }
            })
            .catch(err => {
                console.error(err);
                showErrorToast("Add failed");
            });
    }

    const handleBulkUpload = () => {
        const formData = new FormData();
        formData.append("file", file);

        fetch(`${API_BASE_URL}client_requirements/bulk/upload/`, {
            method: "POST",
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            },
            body: formData,
        })
            .then(res => res.json())
            .then(data => {
                if (data.status === "success") {
                    showSuccessToast("Bulk upload successful!");
                    getSprints(currentProject);
                    getClientRequiremnets(currentProject, { forceRefresh: true });
                    setOpen(false);
                } else {
                    showErrorToast(data.message || "Upload failed");
                }
            })
            .catch(err => {
                console.error(err);
                showErrorToast("Bulk upload failed");
            });
    };

    const handleClearFilter = () => {
        setFilterAssignedTo([]);
        setFilterClient([]);
        setFilterCreatedBy([]);
        setFilterStatus([]);
        setFilterPriority([]);
        setCrSearch('');
    }

    // Move the requirement being edited into a sprint — prompts for Point Type
    // (Epic/Story) and Priority, which are set on the created sprint ticket.
    const handleMoveToSprint = async () => {
        if (!moveDetails.sprint_id || !moveDetails.ticket_type || !moveDetails.priority_id) {
            showErrorToast("Please select Sprint, Point Type and Priority.");
            return;
        }
        setMovingToSprint(true);
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({
                    ...requirementDetails,
                    id: editRequirementId,
                    sprint_id: moveDetails.sprint_id,
                    ticket_type: moveDetails.ticket_type,
                    priority_id: moveDetails.priority_id,
                })
            });
            const data = await res.json();
            if (data.status === "success") {
                showSuccessToast("Requirement moved to sprint!");
                setShowMoveDialog(false);
                setMoveDetails({ sprint_id: '', ticket_type: 'Story', priority_id: '' });
                setShowPopup(false);
                clearRequirementDeatils();
                getSprints(currentProject);
                getClientRequiremnets(currentProject, { forceRefresh: true });
            } else {
                showErrorToast(data.message || "Move to sprint failed");
            }
        } catch (err) {
            console.error(err);
            showErrorToast("Move to sprint failed");
        } finally {
            setMovingToSprint(false);
        }
    };

    const handleRevokeSprintMove = async (requirement) => {
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/revoke-sprint/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
                body: JSON.stringify({ id: requirement.id }),
            });
            const data = await res.json();
            if (data.status === "success") {
                showSuccessToast(data.message || "Sprint move revoked");
                setRevokeConfirm(null);
                getClientRequiremnets(currentProject, { forceRefresh: true });
            } else {
                showErrorToast(data.message || "Revoke failed");
            }
        } catch (err) {
            console.error(err);
            showErrorToast("Revoke failed");
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}-${month}-${year}`;
    };
    
    const filterSx = {
        height: 28, width: 100, border: "1px solid var(--border-secondary)", borderRadius: '6px', fontSize: '0.7rem',
        "& .MuiSelect-select": { display: "flex", alignItems: "center", overflow: "hidden", padding: '4px 8px' }
    };
    const chipBoxSx = { display: "flex", gap: 0.5, overflow: "auto", maxWidth: "100%", "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" };

    const hasAnyFilter = filterStatus.length || filterClient.length || filterCreatedBy.length || filterAssignedTo.length || filterPriority.length;
    const currentProjectName = projects.find(p => String(p.id) === String(currentProject))?.project_name || '';

    return (
        <div className="cr-page">
            {/* ── Header ── */}
            <div className="cr-header">
                <div className="cr-header__left">
                    <h1 className="cr-header__title">Client Requirements</h1>
                    {projects.length > 0 && (
                        <FilterDropdown label="Project" value={currentProject} options={projects} onChange={setCurrentProject} renderLabel={o => o?.project_name || ''} getValue={o => String(o?.id)} />
                    )}
                </div>
                <div className="cr-header__right">
                    <button
                        className="cr-btn cr-btn--outline"
                        onClick={() => getClientRequiremnets(currentProject, { forceRefresh: true })}
                        title="Refresh requirements (bypasses cache)"
                        disabled={!currentProject || loadingRequirements}
                    >
                        <VscRefresh size={14} style={{ marginRight: 4 }} /> Refresh
                    </button>
                    <button className="cr-btn cr-btn--outline" onClick={() => setOpen(true)}>Bulk Upload</button>
                    <button className="cr-btn cr-btn--primary" onClick={handleAdd}>+ Add Requirement</button>
                </div>
            </div>

            {/* Bulk upload */}
            {open && (
                <div className="cr-bulk-bar">
                    <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files[0])} />
                    <button className="cr-btn cr-btn--primary cr-btn--sm" onClick={handleBulkUpload} disabled={!file}>Upload</button>
                    <button className="cr-btn cr-btn--outline cr-btn--sm" onClick={() => setOpen(false)}>Cancel</button>
                </div>
            )}

            {/* ── Toolbar ── */}
            <div className="cr-toolbar">
                <span className="cr-toolbar__count">{crFiltered.length} requirement{crFiltered.length !== 1 ? 's' : ''}</span>
                <div className="cr-toolbar__filters">
                    {currentProject && (
                        <>
                            <FilterDropdown label="Client" value={filterClient[0] || ''} options={clients} onChange={v => setFilterClient(v ? [v] : [])} renderLabel={o => o?.client_name || ''} getValue={o => String(o?.id)} />
                            <FilterDropdown label="Status" value={filterStatus[0] || ''} options={clientStatus} onChange={v => setFilterStatus(v ? [v] : [])} renderLabel={o => o?.client_requirement_status || ''} getValue={o => String(o?.id)} />
                            <FilterDropdown label="Priority" value={filterPriority[0] || ''} options={priorities} onChange={v => setFilterPriority(v ? [v] : [])} />
                            <FilterDropdown label="Created By" value={filterCreatedBy[0] || ''} options={employees} onChange={v => setFilterCreatedBy(v ? [v] : [])} renderLabel={o => o?.name || ''} getValue={o => String(o?.id)} />
                            <FilterDropdown label="Assigned" value={filterAssignedTo[0] || ''} options={employees} onChange={v => setFilterAssignedTo(v ? [v] : [])} renderLabel={o => o?.name || ''} getValue={o => String(o?.id)} />
                            {hasAnyFilter && <button className="cr-btn cr-btn--clear cr-btn--sm" onClick={handleClearFilter}><FiX size={11} /> Clear</button>}
                        </>
                    )}
                </div>
                <div className="cr-toolbar__search">
                    <input type="text" placeholder="Search..." value={crSearch} onChange={(e) => setCrSearch(e.target.value)} />
                    <FiEye size={14} style={{ color: 'var(--text-subtle)' }} />
                </div>
            </div>

            {/* ── Table ── */}
            <div className="cr-table-card">
                <table className="cr-table">
                    <thead>
                        <tr>
                            <th style={{ width: 36 }}>#</th>
                            <th>Req. Code</th>
                            <th>Client</th>
                            <th>Requirement</th>
                            <th>Status</th>
                            <th>Priority</th>
                            <th>Assigned</th>
                            <th>Created By</th>
                            <th>Artifacts</th>
                            <th>Sprint</th>
                            <th style={{ width: 130 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loadingRequirements ? (
                            <tr><td colSpan={11} className="cr-empty"><CircularProgress size={20} sx={{ color: 'var(--accent)' }} /> Loading requirements…</td></tr>
                        ) : crFiltered.length > 0 ? crFiltered.map((r, idx) => (
                            <tr key={r.id || idx} className="cr-row" onClick={() => navigate(`/ClientRequirements/${r.id}`)}>
                                <td className="cr-td--num">{idx + 1}</td>
                                <td>
                                    <span className="cr-code">{r.requirement_code || '-'}</span>
                                    {r.origin === 'SPRINT' && <span className="fd-status-pill fd-status-pill--muted" style={{ marginLeft: 4, fontSize: '0.6rem' }} title="This requirement originated from a sprint ticket">From Sprint</span>}
                                    <br /><span className="cr-date">{formatDate(r.created_time)}</span>
                                </td>
                                <td>{r.client_name || '-'}</td>
                                <td className="cr-td--req">
                                    {r.requirement ? (
                                        <Tooltip
                                            arrow
                                            placement="top-start"
                                            enterDelay={300}
                                            slotProps={{ tooltip: { sx: { maxWidth: 480, fontSize: '0.72rem', lineHeight: 1.55, p: 1.2 } } }}
                                            title={
                                                <>
                                                    <div style={{ fontWeight: 600, whiteSpace: 'pre-wrap' }}>{r.requirement}</div>
                                                    {r.description && (
                                                        <div style={{ marginTop: 6, opacity: 0.85, whiteSpace: 'pre-wrap' }}>{r.description}</div>
                                                    )}
                                                </>
                                            }
                                        >
                                            <span>{r.requirement}</span>
                                        </Tooltip>
                                    ) : '-'}
                                </td>
                                <td onClick={e => e.stopPropagation()}>
                                    <select
                                        className="cr-status-select"
                                        value={r.status_id || ''}
                                        disabled={statusUpdatingId === r.id}
                                        onChange={e => handleStatusChange(r, e.target.value)}
                                        title="Change status"
                                    >
                                        {!r.status_id && <option value="" disabled>Select Status</option>}
                                        {(clientStatus || []).map(s => <option key={s.id} value={s.id}>{s.client_requirement_status}</option>)}
                                    </select>
                                </td>
                                <td><span className={`cr-prio ${r.priority === 'High' ? 'cr-prio--high' : r.priority === 'Low' ? 'cr-prio--low' : ''}`}>{r.priority || '-'}</span></td>
                                <td>{r.assigned_to_name || '-'}</td>
                                <td>{r.created_by || '-'}</td>
                                <td>
                                    <span className={`fd-status-pill ${r.is_moved_to_sprint || r.sprint_name ? 'fd-status-pill--info' : r.generation_status === 'DONE' ? 'fd-status-pill--success' : r.generation_status === 'FAILED' ? 'fd-status-pill--danger' : 'fd-status-pill--muted'}`}>
                                        {r.is_moved_to_sprint || r.sprint_name ? 'In Sprint' : r.generation_status === 'DONE' ? 'Ready' : r.generation_status === 'FAILED' ? 'Failed' : 'Pending'}
                                    </span>
                                </td>
                                <td>{r.sprint_name || <span style={{ color: 'var(--text-subtle)' }}>—</span>}</td>
                                <td onClick={e => e.stopPropagation()}>
                                    <div className="cr-actions">
                                        <button className="fd-action-btn" onClick={() => handleEdit(r)} title="Edit" disabled={r.generation_status === 'DONE' || r.is_moved_to_sprint}><FiEdit2 size={13} /></button>
                                        {(r.is_moved_to_sprint || r.sprint_name) && (
                                            <button className="fd-action-btn" onClick={() => setRevokeConfirm(r)} title="Revoke sprint move — claim back">
                                                <FiRotateCcw size={13} />
                                            </button>
                                        )}
                                        <button className="fd-action-btn fd-action-btn--danger" onClick={() => handleDelete(r.id)} title="Delete"><FiTrash2 size={13} /></button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan={11} className="cr-empty">{crSearch || hasAnyFilter ? 'No results for the current filters' : 'No requirements yet. Add one to get started.'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ══ Slide-over Panel: Add/Edit Requirement ══ */}
            {showPopup && (
                <div className="cr-panel-overlay" onClick={() => { setShowPopup(false); clearRequirementDeatils(); }}>
                    <div className="cr-panel" onClick={e => e.stopPropagation()}>
                        <div className="cr-panel__header">
                            <h2>{editRequirementId ? 'Edit Requirement' : 'New Requirement'}</h2>
                            <button className="cr-panel__close" onClick={() => { setShowPopup(false); clearRequirementDeatils(); }}><FiX size={18} /></button>
                        </div>
                        <div className="cr-panel__body">
                            <div className="cr-form-grid">
                                <div className="cr-form-field cr-form-field--full">
                                    <label>Client</label>
                                    <select name="client_id" value={requirementDetails.client_id} onChange={handleChange}><option value="" disabled>Select Client</option>{(clients || []).map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}</select>
                                    {currentProject && clients.length === 0 && (
                                        <div style={{ fontSize: '0.75rem', color: '#ff9800', marginTop: '4px' }}>
                                            No clients mapped to {currentProjectName || 'this project'}. Add a client to it first.
                                        </div>
                                    )}
                                </div>
                                <div className="cr-form-field">
                                    <label>Status</label>
                                    <select name="status_id" value={requirementDetails.status_id} onChange={handleChange}><option value="" disabled>Select Status</option>{(clientStatus || []).map(s => <option key={s.id} value={s.id}>{s.client_requirement_status}</option>)}</select>
                                </div>
                                <div className="cr-form-field">
                                    <label>Priority</label>
                                    <select name="priority" value={requirementDetails.priority} onChange={handleChange}><option value="" disabled>Select Priority</option><option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option></select>
                                </div>
                                <div className="cr-form-field">
                                    <label>Assigned To</label>
                                    <select name="assigned_to_id" value={requirementDetails.assigned_to_id} onChange={handleChange}><option value="" disabled>Select Assignee</option>{(employees || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
                                </div>
                                {editRequirementId && !requirementDetails.is_moved_to_sprint && (
                                    <div className="cr-form-field">
                                        <label>Sprint</label>
                                        <button type="button" className="cr-btn cr-btn--outline" style={{ width: '100%' }} onClick={() => setShowMoveDialog(true)}>
                                            Move to Sprint…
                                        </button>
                                    </div>
                                )}
                                <div className="cr-form-field cr-form-field--full">
                                    <label>Requirement</label>
                                    <input type="text" name="requirement" value={requirementDetails.requirement} onChange={handleChange} placeholder="Brief requirement summary..." />
                                </div>
                                <div className="cr-form-field cr-form-field--full">
                                    <label>Description</label>
                                    <textarea ref={descRef} name="description" value={requirementDetails.description} onChange={handleChange} placeholder="Detailed description..." rows={4} />
                                </div>
                                <div className="cr-form-field cr-form-field--full">
                                    <label>User Story</label>
                                    <textarea name="user_story" value={requirementDetails.user_story || ''} onChange={handleChange} placeholder="As a <user>, I want <goal> so that <benefit>..." rows={3} />
                                </div>
                                <div className="cr-form-field cr-form-field--full">
                                    <label>Acceptance Criteria</label>
                                    <textarea name="acceptance_criteria" value={requirementDetails.acceptance_criteria || ''} onChange={handleChange} placeholder="Conditions that must be met for this requirement to be accepted..." rows={3} />
                                </div>
                                <div className="cr-form-field cr-form-field--full">
                                    <label>Impact Areas <span className="cr-form-hint">for artifact generation</span></label>
                                    <Autocomplete multiple size="small" options={impactAreaOptions} value={impactAreas} onChange={(_, v) => setImpactAreas(v)}
                                        renderInput={params => <TextField {...params} placeholder="Select impact areas" size="small" />}
                                        renderTags={(value, getTagProps) => value.map((option, index) => <Chip key={option} label={option} {...getTagProps({ index })} size="small" />)}
                                    />
                                </div>
                                <div className="cr-form-field cr-form-field--full">
                                    <label>Attachments</label>
                                    {attachmentFiles.length > 0 && (
                                        <div className="cr-form-att-list">{attachmentFiles.map((f, i) => (
                                            <span key={i} className="cr-form-att-chip">{f.name} <button onClick={() => setAttachmentFiles(prev => prev.filter((_, idx) => idx !== i))}><FiX size={12} /></button></span>
                                        ))}</div>
                                    )}
                                    <label className="cr-upload-trigger">
                                        <input type="file" multiple style={{ display: 'none' }} onChange={e => { if (e.target.files) setAttachmentFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} />
                                        <FiUpload size={13} /> Add Files
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="cr-panel__footer">
                            <button className="cr-btn cr-btn--outline" onClick={() => { setShowPopup(false); clearRequirementDeatils(); }}>Cancel</button>
                            <button className="cr-btn cr-btn--primary" onClick={handleSubmit}>Save</button>
                            <button className="cr-btn cr-btn--accent" onClick={() => {
                                if (!requirementDetails.client_id || !requirementDetails.status_id || !requirementDetails.requirement) {
                                    showErrorToast("Please fill Client, Status, and Requirement before generating.");
                                    return;
                                }
                                setShowConfirmGenerate(true);
                            }}><FiZap size={13} /> Save & Generate</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ Confirm Generate ══ */}
            {showConfirmGenerate && (
                <div className="cr-panel-overlay" onClick={() => setShowConfirmGenerate(false)}>
                    <div className="cr-confirm" onClick={e => e.stopPropagation()}>
                        <h3><FiZap size={16} /> Confirm & Generate Artifact</h3>
                        <p className="cr-confirm__desc">The requirement will be saved and artifact generation will begin.</p>
                        <div className="cr-confirm__details">
                            <div><span>Client</span><strong>{clients.find(c => String(c.id) === String(requirementDetails.client_id))?.client_name || '—'}</strong></div>
                            <div><span>Requirement</span><strong>{requirementDetails.requirement || '—'}</strong></div>
                            {impactAreas.length > 0 && <div><span>Impact Areas</span><strong>{impactAreas.join(', ')}</strong></div>}
                        </div>
                        <div className="cr-confirm__actions">
                            <button className="cr-btn cr-btn--outline" onClick={() => setShowConfirmGenerate(false)}>Cancel</button>
                            <button className="cr-btn cr-btn--accent" onClick={handleSaveAndGenerate}><FiZap size={13} /> Confirm & Generate</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Generating overlay */}
            {generatingArtifact && (
                <div className="cr-panel-overlay cr-panel-overlay--center">
                    <div className="cr-generating">
                        <CircularProgress size={40} sx={{ color: 'var(--accent)' }} />
                        <h3>Generating Artifacts...</h3>
                        <p>This may take a moment. Please wait.</p>
                    </div>
                </div>
            )}

            {/* ══ Move To Sprint (Point Type + Priority prompt) ══ */}
            {showMoveDialog && (
                <div className="cr-panel-overlay" onClick={() => setShowMoveDialog(false)}>
                    <div className="cr-confirm" onClick={e => e.stopPropagation()}>
                        <h3>Move to Sprint</h3>
                        <p className="cr-confirm__desc">
                            Choose the target sprint, the point type and the priority for the
                            ticket that will be created in the sprint.
                        </p>
                        <div className="cr-form-grid">
                            <div className="cr-form-field cr-form-field--full">
                                <label>Sprint</label>
                                <select value={moveDetails.sprint_id} onChange={e => setMoveDetails(prev => ({ ...prev, sprint_id: e.target.value }))}>
                                    <option value="" disabled>Select Sprint</option>
                                    {(sprints || []).map(s => <option key={s.id} value={s.id}>{s.sprint_name}</option>)}
                                </select>
                            </div>
                            <div className="cr-form-field">
                                <label>Point Type</label>
                                <select value={moveDetails.ticket_type} onChange={e => setMoveDetails(prev => ({ ...prev, ticket_type: e.target.value }))}>
                                    <option value="Story">Story</option>
                                    <option value="Epic">Epic</option>
                                </select>
                            </div>
                            <div className="cr-form-field">
                                <label>Priority</label>
                                <select value={moveDetails.priority_id} onChange={e => setMoveDetails(prev => ({ ...prev, priority_id: e.target.value }))}>
                                    <option value="" disabled>Select Priority</option>
                                    {(priorityTypes || []).map(p => <option key={p.id} value={p.id}>{p.priority}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="cr-confirm__actions">
                            <button className="cr-btn cr-btn--outline" onClick={() => setShowMoveDialog(false)}>Cancel</button>
                            <button className="cr-btn cr-btn--primary" onClick={handleMoveToSprint} disabled={movingToSprint || !moveDetails.sprint_id || !moveDetails.priority_id}>
                                {movingToSprint ? 'Moving…' : 'Move to Sprint'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ Revoke Sprint Move Confirmation ══ */}
            {revokeConfirm && (
                <div className="cr-panel-overlay" onClick={() => setRevokeConfirm(null)}>
                    <div className="cr-confirm" onClick={e => e.stopPropagation()}>
                        <h3><FiRotateCcw size={16} /> Move Back to Requirements</h3>
                        <p className="cr-confirm__desc">
                            The sprint ticket will be removed from its sprint and its status reset
                            to "To Do". The requirement returns to the queue — if it is moved to a
                            sprint again later, the same ticket is reused (no duplicates).
                        </p>
                        <div className="cr-confirm__details">
                            <div><span>Requirement</span><strong>{revokeConfirm.requirement_code || '—'}</strong></div>
                            <div><span>Sprint</span><strong>{revokeConfirm.sprint_name || '—'}</strong></div>
                            <div><span>Summary</span><strong>{revokeConfirm.requirement || '—'}</strong></div>
                        </div>
                        <div className="cr-confirm__actions">
                            <button className="cr-btn cr-btn--outline" onClick={() => setRevokeConfirm(null)}>Cancel</button>
                            <button className="cr-btn cr-btn--accent" onClick={() => handleRevokeSprintMove(revokeConfirm)}>
                                <FiRotateCcw size={13} /> Revoke & Claim Back
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientRequirements;